import { useEffect, useState } from 'react'
import { Col, Flex, Switch, Table, Typography } from 'antd'
import styled from 'styled-components'
import { colours } from '@qflow/theme'

import { useApi } from 'admin/lib/Api'
import { ResponsiveWrapper } from 'admin/lib/Common/StyledElements'
import { Flag } from 'admin/lib/Icons/Flag'
import { Layout } from 'admin/lib/Layouts'
import { ScreenSpin } from 'admin/lib/ScreenSpin'

export function RecordsSummary() {
  const [isLoading, setIsLoading] = useState(true)
  const [showClosedOut, setShowClosedOut] = useState(false)
  const [data, setData] = useState([])
  const api = useApi()

  useEffect(() => {
    const handleLoad = async () => {
      return await api.getRecordsSummary()
    }

    setIsLoading(true)
    handleLoad()
      .then(value => {
        setData(value[0])
        setIsLoading(false)
      })
      .catch(() => {
        setData([])
      })
  }, [api])

  const formatTableData = () => {
    let formattedData = []
    const totals = { key: 'total', name: 'TOTAL', account: '', country: '' }
    const IGNORE_ACCOUNT_ARRAY = [
      '26532b6e-ac23-4142-b74d-c48d6cb33d7c', // PROD - Demo UK
      '7bad3c12-4d21-4530-92c7-2d507b071921', // PROD - Demo US
      '269842d3-cfea-47e8-8678-dc27a903f1d7', // PROD - Main Test Account
      '02dedf27-1fc5-44e4-ba5f-1c166cf66898' // PROD - Qflow data admin demo
    ]

    if (data && data.projectSummaries && data.projectSummaries.length > 0) {
      formattedData = data.projectSummaries
        .filter(item => !IGNORE_ACCOUNT_ARRAY.includes(item.account.accountId))
        .filter(item => (showClosedOut ? true : !item.project.isClosedOut))
        .map(item => ({
          key: item.project.projectId,
          name: item.project.name,
          account: item.account.name,
          country: item.project.country,
          isClosedOut: item.project.isClosedOut,
          New: item.recordCountsByStatus.New
            ? item.recordCountsByStatus.New
            : 0,
          Failed: item.recordCountsByStatus.Failed
            ? item.recordCountsByStatus.Failed
            : 0,
          Escalated: item.recordCountsByStatus.Escalated
            ? item.recordCountsByStatus.Escalated
            : 0,
          Published: item.recordCountsByStatus.Published
            ? item.recordCountsByStatus.Published
            : 0,
          AutoPublished: item.recordCountsByStatus.AutoPublished
            ? item.recordCountsByStatus.AutoPublished
            : 0,
          Removed: item.recordCountsByStatus.Removed
            ? item.recordCountsByStatus.Removed
            : 0,
          Void: item.recordCountsByStatus.Void
            ? item.recordCountsByStatus.Void
            : 0
        }))

      // Get Totals
      totals.New = formattedData.reduce((accum, item) => accum + item.New, 0)
      totals.Failed = formattedData.reduce(
        (accum, item) => accum + item.Failed,
        0
      )
      totals.Escalated = formattedData.reduce(
        (accum, item) => accum + item.Escalated,
        0
      )
      totals.Published = formattedData.reduce(
        (accum, item) => accum + item.Published,
        0
      )
      totals.AutoPublished = formattedData.reduce(
        (accum, item) => accum + item.AutoPublished,
        0
      )
      totals.Removed = formattedData.reduce(
        (accum, item) => accum + item.Removed,
        0
      )
      totals.Void = formattedData.reduce((accum, item) => accum + item.Void, 0)
    }

    return [totals, ...formattedData]
  }

  const columns = [
    {
      title: '',
      dataIndex: 'country',
      width: '36px',
      render: country => {
        return (
          <Flag
            country={country}
            style={{ width: '28px', margin: '0 -8px -2px 0' }}
          />
        )
      }
    },
    {
      title: 'Project',
      dataIndex: 'name',
      sorter: (a, b) => (b.name === 'TOTAL' ? 1 : a.name.localeCompare(b.name)),
      sortDirections: ['ascend'],
      render: (value, record) => (
        <>
          <Col>
            <Typography.Text strong={true}>{value}</Typography.Text>
          </Col>
          <Col>
            <Typography.Text italic={true}>{record.account}</Typography.Text>
          </Col>
        </>
      )
    },
    {
      title: '',
      dataIndex: 'isClosedOut',
      width: '54px',
      onCell: () => ({
        style: { padding: '0', margin: '0', textAlign: 'center' }
      }),
      render: value => {
        if (value) {
          return <ClosedOutBadge strong={true}>Closed Out</ClosedOutBadge>
        }
        return null
      }
    },
    {
      title: 'New',
      dataIndex: 'New',
      width: '100px',
      align: 'center',
      sorter: (a, b) => a.New - b.New,
      sortDirections: ['descend']
    },
    {
      title: 'Failed',
      dataIndex: 'Failed',
      width: '100px',
      align: 'center',
      sorter: (a, b) => a.Failed - b.Failed,
      sortDirections: ['descend'],
      defaultSortOrder: 'descend'
    },
    {
      title: 'Escalated',
      dataIndex: 'Escalated',
      width: '115px',
      align: 'center',
      sorter: (a, b) => a.Escalated - b.Escalated,
      sortDirections: ['descend']
    },
    {
      title: 'Published',
      dataIndex: 'Published',
      width: '115px',
      align: 'center',
      sorter: (a, b) => a.Published - b.Published,
      sortDirections: ['descend']
    },
    {
      title: 'AutoPublished',
      dataIndex: 'AutoPublished',
      width: '130px',
      align: 'center',
      sorter: (a, b) => a.AutoPublished - b.AutoPublished,
      sortDirections: ['descend']
    },
    {
      title: 'Removed',
      dataIndex: 'Removed',
      width: '115px',
      align: 'center',
      sorter: (a, b) => a.Removed - b.Removed,
      sortDirections: ['descend']
    },
    {
      title: 'Void',
      dataIndex: 'Void',
      width: '100px',
      align: 'center',
      sorter: (a, b) => a.Void - b.Void,
      sortDirections: ['descend']
    }
  ]

  return (
    <Layout>
      <ResponsiveWrapper style={{ margin: '1.5rem auto' }}>
        <div>
          <h1>Records Summary</h1>
          <Flex align="center" gap={'1rem'}>
            <Typography.Title level={5} style={{ margin: '0 0 0 0.25rem' }}>
              Show Closed Out
            </Typography.Title>
            <Switch
              checked={showClosedOut}
              checkedChildren="Yes"
              unCheckedChildren="No"
              onChange={checked => setShowClosedOut(checked)}
            />
          </Flex>
        </div>
        {isLoading && <ScreenSpin id="userLoadingSpinner" />}
        {!isLoading && (
          <Table
            size="middle"
            columns={columns}
            dataSource={formatTableData()}
            pagination={false}
            scroll={{ y: 'calc(100vh - 248px)' }}
            sticky
          />
        )}
      </ResponsiveWrapper>
    </Layout>
  )
}

export const ClosedOutBadge = styled(Typography.Text)`
  display: inline-block;
  min-width: 32px;
  background-color: ${colours.WHITE};
  color: ${colours.RED} !important;
  border: 1px solid ${colours.RED};
  padding: 1px 4px;
  border-radius: 5px;
  margin: 0 0 1px;
`
